import { Box, ColorOptions, Spacer, TextNew } from '@jfs/ui-new'
import { dateFormatter2 as dateFormatter, DateFormatTypes, getRangeDate } from '@jfs/utils'
import * as React from 'react'
import { FC, ReactElement, useState } from 'react'
import ReactDOM from 'react-dom'
import { Dimensions, GestureResponderEvent, LayoutAnimation } from 'react-native'
import { staticStrings } from '../../data/static/strings'
import { Routes } from '../../nav/routes'
import { calendarWebHeight, calendarWebWidth, Colors } from '../../util/util'
import CalendarViewModal from '../CalendarViewModal'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import { CalendarTypes } from './../Calendar'
import { styles } from './styles'

export interface DateType {
  fromDate: Date
  toDate?: Date
  position?: { left?: number; top?: number; visible: boolean }
}

interface layoutPosition {
  clientY: number
  offsetY: number
  clientX: number
  offsetX: number
}

const deflayoutPosition = {
  clientY: 0,
  offsetY: 0,
  clientX: 0,
  offsetX: 0,
}
interface DateSelectorProp {
  value: DateType
  onPress?: (data: string) => void
  path?: string
  onDateSelected?: (dateObj: DateType) => void
  setNewDate: (data: any) => void
  defaultMinDate?: Date
  isFocused: boolean
  defaultMaxDate?: Date
  maxDuration?: number
  scrollToCurrentDate?: boolean
  sendError?: any
  screenName?: string
}

const DateSelector: FC<DateSelectorProp> = ({
  value: { fromDate, toDate, position },
  onDateSelected,
  setNewDate,
  path,
  defaultMinDate,
  isFocused,
  defaultMaxDate,
  maxDuration,
  scrollToCurrentDate,
  sendError,
  screenName,
}: DateSelectorProp) => {
  const [layoutPosition, setPosition] = useState<layoutPosition>(deflayoutPosition)
  const windowWidth = Dimensions.get('window').width
  const leftPos = layoutPosition?.clientX - layoutPosition?.offsetX
  const remainingWidth = windowWidth - calendarWebWidth
  const isWebCalendarOpenInNative = windowWidth - calendarWebWidth < 200
  const isFromBulkRefund = path === Routes.BULK_REFUND
  const isSubscriptionFilter = path === Routes.SUBSCRIPTION_FILTER
  const isVault = path === Routes.VAULT_TOKENIZATION

  const onDateSelection = (firstdate: Date, secondDate?: Date): void => {
    if (secondDate) {
      const dateObj: DateType = { fromDate: firstdate, toDate: secondDate }
      if (onDateSelected) {
        onDateSelected(dateObj)
      }
      setNewDate({
        fromDate: firstdate,
        toDate: secondDate,
        position: {
          top: position?.top,
          left: position?.left,
          visible: !position?.visible,
        },
      })
    }
  }

  const handleCancel = (): void => {
    handleDateSelected()
  }

  const handleDateSelected = () => {
    setNewDate({
      fromDate,
      toDate,
      position: {
        top: position?.top,
        left: position?.left,
        visible: !position?.visible,
      },
    })
  }

  const onPressButton = (evt: GestureResponderEvent): void => {
    setPosition(evt?.nativeEvent)
    setNewDate({
      fromDate,
      toDate,
      position: {
        top: evt.nativeEvent.pageY - evt.nativeEvent.locationY + styles.dimens.top,
        left: evt.nativeEvent.pageX - evt.nativeEvent.locationX - styles.dimens.right,
        visible: !position?.visible,
      },
    })
  }

  const returnLeftValue = () => {
    if (isSubscriptionFilter) {
      return undefined
    } else {
      if (remainingWidth < calendarWebWidth || isWebCalendarOpenInNative) {
        return undefined
      } else if (!isWebCalendarOpenInNative && !isFromBulkRefund && !isVault) {
        return leftPos
      }
    }
  }

  const returnTopValue = () => {
    if (isSubscriptionFilter) {
      return layoutPosition?.clientY - layoutPosition?.offsetY + 30
    } else if (path === Routes.VAULT_TOKENIZATION || screenName === Routes.REFUNDS) {
      const sHeight = Dimensions.get('window').height
      const spaceAvailableAtBottom = sHeight - layoutPosition?.clientY + layoutPosition?.offsetY
      if (calendarWebHeight > spaceAvailableAtBottom) {
        const xtraSpace = calendarWebHeight - spaceAvailableAtBottom
        return layoutPosition?.clientY - layoutPosition?.offsetY - xtraSpace - 16
      }
      return layoutPosition?.clientY - layoutPosition?.offsetY
    } else {
      return layoutPosition?.clientY - layoutPosition?.offsetY + 30
    }
  }

  const returnRightValue = () => {
    if (isSubscriptionFilter) {
      if (remainingWidth < layoutPosition?.clientX && isWebCalendarOpenInNative) {
        return 20
      } else {
        return windowWidth - layoutPosition?.clientX - layoutPosition?.offsetX + 70
      }
    } else {
      if (remainingWidth < calendarWebWidth || isWebCalendarOpenInNative || isFromBulkRefund || isVault) {
        return 17
      } else {
        return undefined
      }
    }
  }

  const onLayout = (event: LayoutAnimation) => {
    const { layout } = event?.nativeEvent
    const customLayoutPosition = {
      clientY: layout?.top + 10,
      offsetY: layout?.y,
      clientX: layout?.left,
      offsetX: layout?.x,
    }
    setPosition(customLayoutPosition ?? null)
  }

  const renderCalendarModal = (): ReactElement => {
    return (
      <ModBox
        {...styles.modal}
        underlayColor="TRANSPARENT"
        show={position?.visible}
        testID="cancel-calender"
        onPress={handleCancel}>
        <ModBox underlayColor={'TRANSPARENT'} {...styles.overlayStyle}>
          <ModBox {...styles.dropdownStyle} right={returnRightValue()} left={returnLeftValue()} top={returnTopValue()}>
            <CalendarViewModal
              onDateSelection={onDateSelection}
              dateRange={getRangeDate(fromDate, toDate)}
              type={CalendarTypes.HISTORY}
              onCancel={handleCancel}
              defaultMinDate={defaultMinDate}
              defaultMaxDate={defaultMaxDate}
              maxDuration={maxDuration}
              scrollToCurrentDate={scrollToCurrentDate}
              sendError={sendError}
            />
          </ModBox>
        </ModBox>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const getDate = (): ReactElement => {
    if (toDate && fromDate.toLocaleDateString() !== toDate.toLocaleDateString()) {
      return (
        <ModBox {...styles.row}>
          <Spacer direction="horizontal" size={8} />
          <TextNew type={'body12'} color={Colors.HELPER_TEXT as ColorOptions}>
            {staticStrings.to}
          </TextNew>
          <Spacer direction="horizontal" size={8} />
          <TextNew type={'body12'} color={'BLACK_21'}>
            {dateFormatter(toDate, DateFormatTypes.DATE_STRING)}
          </TextNew>
        </ModBox>
      )
    }
    return <></>
  }

  const getFromDate = () => {
    return dateFormatter(fromDate, DateFormatTypes.DATE_STRING)
  }

  return (
    <Box underlayColor={'TRANSPARENT'} testID="date-selection" onLayout={onLayout} onPress={onPressButton} {...styles.container}>
      <ModBox {...styles.tabsCenter}>
        <ModBox {...styles.row}>
          <TextNew type={'body12'} color={'BLACK_21'}>
            {getFromDate()}
          </TextNew>
          {getDate()}
        </ModBox>
        {isFocused && renderPortal()}
      </ModBox>
    </Box>
  )
}

export { DateSelector }
